import pino from 'pino';

import {registerOpenTelemetryLoggerBridge} from '../../otel';

import {forwardLoggerTelemetry, getLoggerTelemetryFields} from './bridge';

// Register the OpenTelemetry logger bridge to forward logs to the OpenTelemetry collector
registerOpenTelemetryLoggerBridge();

const isBrowserRuntime = typeof window !== 'undefined';

const pinoConfig: pino.LoggerOptions = {
  level: process.env.LOG_LEVEL || 'info',
  messageKey: 'message',
  base: undefined,
  timestamp: pino.stdTimeFunctions.isoTime,
  formatters: {
    level(label) {
      return {level: label};
    },
  },
  ...(isBrowserRuntime
    ? {}
    : {
        hooks: {
          logMethod(args, method, level) {
            forwardLoggerTelemetry(args, level);
            method.apply(this, args);
          },
        },
      }),
  mixin() {
    return getLoggerTelemetryFields();
  },
};

const baseLogger =
  typeof pino.destination === 'function'
    ? pino(
        pinoConfig,
        pino.destination({
          dest: 1,
          sync: false,
        }),
      )
    : pino(pinoConfig);

const browserLevelValues: Record<pino.Level, number> = {
  trace: pino.levels.values.trace,
  debug: pino.levels.values.debug,
  info: pino.levels.values.info,
  warn: pino.levels.values.warn,
  error: pino.levels.values.error,
  fatal: pino.levels.values.fatal,
};

const attachBrowserTelemetryBridge = (
  loggerInstance: pino.Logger,
): pino.Logger => {
  if (!isBrowserRuntime) {
    return loggerInstance;
  }

  const browserLogger = loggerInstance as pino.Logger &
    Record<string, pino.LogFn>;

  (Object.keys(browserLevelValues) as pino.Level[]).forEach((level) => {
    const originalMethod = browserLogger[level].bind(browserLogger);

    browserLogger[level] = ((...args: Parameters<pino.LogFn>) => {
      forwardLoggerTelemetry(args, browserLevelValues[level]);

      return originalMethod(...args);
    }) as pino.LogFn;
  });

  return browserLogger;
};

export const logger = attachBrowserTelemetryBridge(baseLogger);
